import { useCallback, useState } from 'react';

import { useElements } from '@stripe/react-stripe-js';
import { SetupIntentResult, Stripe } from '@stripe/stripe-js';

import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Submits the payment element form and handles its result.
 */
export const usePaymentElementSubmission = (params: Params) => {
  const { stripe, elements, setDefaultPaymentMethod, notifyStripeError, onClose } = params;

  const { track } = useTelemetry();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(async () => {
    if (!stripe || !elements) return;

    setIsSubmitting(true);

    const result = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });

    // Stripe returned an error (e.g. invalid payment method). Surface it to the
    // user and keep the sheet open so they can correct the card and retry.
    if (result.error) {
      setIsSubmitting(false);
      notifyStripeError(result.error.message, { canShowErrorMessage: true });

      return;
    }

    const paymentMethodId = getPaymentMethodId(result);

    if (paymentMethodId) await setDefaultPaymentMethod(paymentMethodId);

    track('payments.edited');
    setIsSubmitting(false);
    onClose();
  }, [stripe, elements, setDefaultPaymentMethod, notifyStripeError, onClose, track]);

  return { isSubmitting, handleSubmit };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getPaymentMethodId = (result: SetupIntentResult) => {
  if (typeof result.setupIntent?.payment_method === 'string') {
    return result.setupIntent?.payment_method;
  }

  return result.setupIntent?.payment_method?.id;
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  stripe: Stripe | null;
  elements: ReturnType<typeof useElements>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<void>;
  notifyStripeError: (error: string | undefined, options: { canShowErrorMessage: boolean }) => void;
  onClose: () => void;
};
